/**
 * `Tabs` — `#intelTabs`, Amendment 1 §1/§A's three square tabs directly under the dock
 * header: "icon above label … active state = filled tile and heavier label, hover per the
 * portal's existing style, keyboard focusable (`role=\"tablist\"`, arrow keys move focus)."
 *
 * The same roving-tabindex `tablist` the Data Manager's rail and the catalogue record's
 * About/Data tabs already use (`ManagerPage`, `DatasetRecord`) — arrow keys move focus *and*
 * selection together (there is nothing behind an unselected tab worth previewing without
 * committing to it, unlike a browser's own tab strip), Home/End jump to the ends, and a
 * native `<button>` gives Enter/Space for free.
 */
import { useCallback, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../../lib/icons';
import { useAppStore, type IntelTab } from '../../lib/state';

const TAB_ORDER: readonly IntelTab[] = ['usecases', 'tools', 'help'];

/** From the rail's own set (`lib/icons.tsx`) — no icon family of its own. */
const TAB_ICON: Record<IntelTab, IconName> = {
  usecases: 'chart',
  tools: 'wrench',
  help: 'info',
};

export function Tabs() {
  const { t } = useTranslation();
  const tab = useAppStore((s) => s.intel.tab);
  const setIntelTab = useAppStore((s) => s.setIntelTab);
  const listRef = useRef<HTMLDivElement>(null);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const i = TAB_ORDER.indexOf(tab);
      let next = i;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % TAB_ORDER.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
        next = (i - 1 + TAB_ORDER.length) % TAB_ORDER.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = TAB_ORDER.length - 1;
      else return;
      e.preventDefault();
      const nextTab = TAB_ORDER[next]!;
      setIntelTab(nextTab);
      listRef.current?.querySelector<HTMLButtonElement>(`#intelTab-${nextTab}`)?.focus();
    },
    [tab, setIntelTab],
  );

  return (
    <div
      id="intelTabs"
      className="intel-tabs"
      role="tablist"
      aria-label={t('intel.tabs.label')}
      ref={listRef}
      onKeyDown={onKeyDown}
    >
      {TAB_ORDER.map((name) => (
        <button
          key={name}
          type="button"
          role="tab"
          id={`intelTab-${name}`}
          className={tab === name ? 'intel-tab on' : 'intel-tab'}
          aria-selected={tab === name}
          aria-controls={`intelPanel-${name}`}
          tabIndex={tab === name ? 0 : -1}
          onClick={() => setIntelTab(name)}
        >
          <Icon name={TAB_ICON[name]} size={18} />
          <span className="intel-tab-label">{t(`intel.tabs.${name}`)}</span>
        </button>
      ))}
    </div>
  );
}

export default Tabs;
